import { useMemo } from "react";

import {
  type SyncableColumnFragment as SyncableColumn,
  useListJourneyContextVariableColumnsQuery,
  useListJourneySyncableEventColumnsQuery,
} from "src/graphql";

/**
 * Fetches journey event columns (based on entry node relationship) and journey context variable columns,
 * then returns a merged array or error (if any) along with loading state .
 */
export function useJourneySyncableColumns(
  journeyId?: string | null,
  entryEventRelationshipId?: string | number | null,
): {
  journeyColumns: SyncableColumn[];
  isLoading: boolean;
  error: Error | null;
} {
  const {
    data: eventColumns,
    isLoading: isLoadingEventColumns,
    error: eventColumnsError,
  } = useListJourneySyncableEventColumnsQuery(
    {
      eventRelationshipId: entryEventRelationshipId?.toString() || "",
      journeyId: journeyId ?? "",
    },
    {
      enabled: Boolean(entryEventRelationshipId && journeyId),
      select: (data) => data.listJourneySyncableEventColumns,
    },
  );

  const {
    data: contextColumnsQuery,
    isLoading: isLoadingContextVariableColumns,
    error: contextColumnsError,
  } = useListJourneyContextVariableColumnsQuery(
    { journeyId: journeyId ?? "" },
    { enabled: Boolean(journeyId) },
  );

  const journeyColumns = useMemo<SyncableColumn[]>(() => {
    return [
      ...(eventColumns || []),
      ...(contextColumnsQuery?.listJourneySyncableContextColumns || []),
    ];
  }, [eventColumns, contextColumnsQuery]);

  return {
    journeyColumns,
    error: eventColumnsError || contextColumnsError,
    isLoading: isLoadingEventColumns || isLoadingContextVariableColumns,
  };
}
